import SectionCard from '../ui/SectionCard';
import SelectableCard from '../ui/SelectableCard';
import RadioCardGroup from '../ui/RadioCardGroup';

/**
 * Add-on subscriptions, billed on the same term as the primary plan.
 *
 * Currently hidden in production behind REACT_APP_FEATURE_ADDONS. It was previously disabled
 * with `{false && ...}`, which left the code unreachable, unlinted, and impossible to run
 * anywhere. resolveCart honors the same flag, so an add-on can never be charged while this
 * section is hidden.
 *
 * `enabled` is a PROP rather than a module-level FEATURES read so this can be rendered in a
 * test both ways without module-registry games. That matters: the flagged path has never run
 * against the current pricing code, so it needs real coverage before being switched on.
 *
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {object[]} props.addons        already filtered by visibility
 * @param {object} props.selected        { [addonId]: boolean }
 * @param {string} props.term            'monthly' | 'yearly'
 * @param {Function} props.onToggle      (addonId) => void
 */
export default function AddonSubscriptionsSection({ enabled, addons, selected, term, onToggle }) {
  if (!enabled || !addons?.length) return null;

  return (
    <SectionCard
      title="Add-on Subscriptions"
      icon="adjustments"
      description="Add additional features to your plan (same billing period):"
    >
      <RadioCardGroup legend="Add-on subscriptions" className="flex flex-col rounded-sm border border-gray-200">
        {addons.map((addon) => {
          const termObj = addon.terms?.find((t) => t.id === term);
          return (
            <SelectableCard
              key={addon.id}
              density="row"
              type="checkbox"
              id={`addon-${addon.id}`}
              name={`addon-${addon.id}`}
              value={addon.id}
              checked={!!selected[addon.id]}
              onChange={() => onToggle(addon.id)}
              title={addon.name}
              subtitle={addon.description}
              // No `|| 0` fallback: the old markup rendered "$0" when a term did not resolve,
              // making a config error look like a free add-on.
              priceLabel={termObj ? `$${termObj.price_display}` : undefined}
              priceSuffix={term === 'monthly' ? '/mo' : '/yr'}
            />
          );
        })}
      </RadioCardGroup>
    </SectionCard>
  );
}
